/**
 * Safe Storage Utilities for iOS WebKit Compatibility
 * 
 * iOS Safari browser mode can block localStorage/sessionStorage access due to:
 * - Private browsing mode
 * - Security settings
 * - WebKit restrictions
 * 
 * These utilities provide safe access that won't crash the app.
 */

/**
 * Check if storage is available and accessible
 */
function isStorageAvailable(type: 'localStorage' | 'sessionStorage'): boolean {
  if (typeof window === 'undefined') return false;
  
  try {
    const storage = window[type];
    const testKey = '__storage_test__';
    storage.setItem(testKey, 'test');
    storage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * In-memory fallback storage for when localStorage is blocked
 */
class MemoryStorage implements Storage {
  private data: Map<string, string> = new Map();

  get length(): number {
    return this.data.size;
  }

  clear(): void {
    this.data.clear();
  }

  getItem(key: string): string | null {
    return this.data.get(key) ?? null;
  }

  key(index: number): string | null {
    const keys = Array.from(this.data.keys());
    return keys[index] ?? null;
  }

  removeItem(key: string): void {
    this.data.delete(key);
  }

  setItem(key: string, value: string): void {
    this.data.set(key, value);
  }
}

// Create safe storage instances
let safeLocalStorage: Storage | null = null;
let safeSessionStorage: Storage | null = null;

/**
 * Get safe localStorage (falls back to memory storage if blocked)
 */
export function getSafeLocalStorage(): Storage {
  if (safeLocalStorage) return safeLocalStorage;

  try {
    if (isStorageAvailable('localStorage')) {
      safeLocalStorage = window.localStorage;
    } else {
      console.warn('[SafeStorage] localStorage not available, using memory fallback');
      safeLocalStorage = new MemoryStorage();
    }
  } catch (e) {
    console.warn('[SafeStorage] localStorage access blocked, using memory fallback:', e);
    safeLocalStorage = new MemoryStorage();
  }

  return safeLocalStorage;
}

/**
 * Get safe sessionStorage (falls back to memory storage if blocked)
 */
export function getSafeSessionStorage(): Storage {
  if (safeSessionStorage) return safeSessionStorage;

  try {
    if (isStorageAvailable('sessionStorage')) {
      safeSessionStorage = window.sessionStorage;
    } else {
      console.warn('[SafeStorage] sessionStorage not available, using memory fallback');
      safeSessionStorage = new MemoryStorage();
    }
  } catch (e) {
    console.warn('[SafeStorage] sessionStorage access blocked, using memory fallback:', e);
    safeSessionStorage = new MemoryStorage();
  }

  return safeSessionStorage;
}

/**
 * Safe localStorage.getItem with error handling
 */
export function safeGetItem(key: string): string | null {
  try {
    const storage = getSafeLocalStorage();
    return storage.getItem(key);
  } catch (e) {
    console.error('[SafeStorage] Error getting item:', key, e);
    return null;
  }
}

/**
 * Safe localStorage.setItem with error handling
 */
export function safeSetItem(key: string, value: string): boolean {
  try {
    const storage = getSafeLocalStorage();
    storage.setItem(key, value);
    return true;
  } catch (e) {
    console.error('[SafeStorage] Error setting item:', key, e);
    return false;
  }
}

/**
 * Safe localStorage.removeItem with error handling
 */
export function safeRemoveItem(key: string): boolean {
  try {
    const storage = getSafeLocalStorage();
    storage.removeItem(key);
    return true;
  } catch (e) {
    console.error('[SafeStorage] Error removing item:', key, e);
    return false;
  }
}

/**
 * Safe localStorage.clear with error handling
 */
export function safeClearStorage(): boolean {
  try {
    const storage = getSafeLocalStorage();
    storage.clear();
    return true;
  } catch (e) {
    console.error('[SafeStorage] Error clearing storage:', e);
    return false;
  }
}
